import { getCmsConstants, preloadGlobeData, preloadPage, preloadResources } from './api';

type RouteLoader = () => Promise<any>;

export const routeLoaders: Record<string, RouteLoader> = {
    '/': () => import('../pages/LandingPage'),
    '/asset-map': () => import('../pages/GlobePage'),
    '/contact-us': () => import('../pages/ContactPage'),
    '/privacy-policy': () => import('../pages/PrivacyPolicyPage'),
    '/resources': () => import('../pages/ResourcesPage'),
    '/advanced-manufacturing': () => import('../pages/clusters/AdvancedManufacturingPage'),
    '/cybersecurity': () => import('../pages/clusters/CybersecurityPage'),
    '/architecture-engineering': () => import('../pages/clusters/ArchitectureEngineeringPage'),
    '/financial-services-insurance': () => import('../pages/clusters/FinancialServicesPage'),
    '/human-life-sciences': () => import('../pages/clusters/HumanLifeSciencesPage'),
    '/plant-&-animal-sciences': () => import('../pages/clusters/PlantAnimalSciencesPage'),
    '/technology': () => import('../pages/clusters/TechnologyPage'),
    '/digital-health': () => import('../pages/clusters/DigitalHealthPage'),
    '/datasphere': () => import('../pages/projects/DataSpherePage'),
    '/heartland-cyber-range': () => import('../pages/projects/CyberRangePage'),
    '/secure-space-campus': () => import('../pages/projects/SecureSpaceCampusPage'),
    '/talentsphere': () => import('../pages/projects/TalentSpherePage'),
    '/grow-with-google': () => import('../pages/GrowWithGooglePage'),
    '/our-approach': () => import('../pages/OurApproachPage'),
    '/defend-tomorrow': () => import('../pages/CyberRangeLandingPage'),
    '/operation287': () => import('../pages/Operation287'),
    '/cms-preview': () => import('../pages/CmsPreviewPage'),
    '/pages/:slug': () => import('../pages/CmsPageView'),
    '/:slug': () => import('../pages/CmsPageView'),
    '/explore-data': () => import('../pages/DataPage'),
    '/OnePage': () => import('../pages/OnePage'),
    '/image-host': () => import('../pages/ImageHost'),
    '/dashboard-layout': () => import('../layouts/DashboardLayout'),
    '/dashboard': () => import('../pages/dashboard/DashboardPage'),
    '/dashboard/asset-map': () => import('../pages/dashboard/GlobeDashboardPage'),
    '/dashboard/resources': () => import('../pages/dashboard/ResourceDashboardPage'),
    '/dashboard/banner': () => import('../pages/dashboard/BannerContactPage'),
    '/dashboard/pages': () => import('../pages/dashboard/PageBuilderPage'),
    '/dashboard/resource-templates': () => import('../pages/dashboard/ResourceTemplatePage'),
    '/dashboard/content-deploy': () => import('../pages/dashboard/ContentDeployPage'),
    '/dashboard/git-merge': () => import('../pages/dashboard/GitMergePage'),
    '/dashboard/constants': () => import('../pages/dashboard/ConstantsPage'),
    '/dashboard/media': () => import('../pages/dashboard/MediaLibraryPage'),
    '/dashboard/users': () => import('../pages/dashboard/UserManagementPage'),
    '/dashboard/account': () => import('../pages/dashboard/AccountSecurityPage'),
    '/dashboard/activity': () => import('../pages/dashboard/ActivityLogPage'),
    '/admin/login': () => import('../pages/dashboard/LoginPage'),
    '/admin/register': () => import('../pages/dashboard/RegisterPage'),
    '/admin/logout': () => import('../pages/dashboard/LogoutPage'),
};

const routeToCmsSlug: Record<string, string> = {
    '/': 'home',
    '/asset-map': 'asset-map',
    '/contact-us': 'contact-us',
    '/privacy-policy': 'privacy-policy',
    '/resources': 'resources',
    '/advanced-manufacturing': 'advanced-manufacturing',
    '/cybersecurity': 'cybersecurity',
    '/architecture-engineering': 'architecture-engineering',
    '/financial-services-insurance': 'financial-services-insurance',
    '/human-life-sciences': 'human-life-sciences',
    '/plant-&-animal-sciences': 'plant-&-animal-sciences',
    '/technology': 'technology',
    '/digital-health': 'digital-health',
    '/datasphere': 'datasphere',
    '/heartland-cyber-range': 'heartland-cyber-range',
    '/secure-space-campus': 'secure-space-campus',
    '/talentsphere': 'talentsphere',
    '/grow-with-google': 'grow-with-google',
    '/our-approach': 'our-approach',
    '/defend-tomorrow': 'defend-tomorrow',
    '/defendtomorrow': 'defend-tomorrow',
    '/operation287': 'operation287',
    '/image-host': 'image-host',
    '/OnePage': 'onepage',
};

const warmedChunks = new Set<string>();
const clusterSlugs = new Set([
    'advanced-manufacturing',
    'architecture-engineering',
    'cybersecurity',
    'digital-health',
    'financial-services-insurance',
    'human-life-sciences',
    'plant-&-animal-sciences',
    'technology',
]);

export function normalizeRoutePath(to: string): string {
    if (!to || to.startsWith('#')) return '';
    let path = to;
    if (/^https?:\/\//i.test(path)) {
        if (typeof window === 'undefined') return '';
        const url = new URL(path);
        if (url.origin !== window.location.origin) return '';
        path = url.pathname;
    }
    path = path.split(/[?#]/)[0] || '/';
    return path !== '/' ? path.replace(/\/+$/, '') : '/';
}

function loaderKeyForPath(path: string): string {
    if (routeLoaders[path]) return path;
    if (path.startsWith('/explore-data')) return '/explore-data';
    if (path.startsWith('/pages/')) return '/pages/:slug';
    if (path.startsWith('/dashboard/')) return path;
    if (path === '/defendtomorrow') return '/defend-tomorrow';
    return '/:slug';
}

export function cmsSlugForPath(to: string): string | null {
    const path = normalizeRoutePath(to);
    if (!path || path.startsWith('/admin') || path.startsWith('/dashboard') || path === '/cms-preview') {
        return null;
    }
    if (path.startsWith('/explore-data')) return 'explore-data';
    if (path.startsWith('/pages/')) {
        const slug = path.split('/').filter(Boolean)[1];
        return slug ? decodeURIComponent(slug) : null;
    }
    if (routeToCmsSlug[path]) return routeToCmsSlug[path];
    const slug = path.split('/').filter(Boolean)[0];
    return slug ? decodeURIComponent(slug) : null;
}

export function preloadRoute(to?: string): void {
    if (!to) return;
    const path = normalizeRoutePath(to);
    if (!path) return;

    const loaderKey = loaderKeyForPath(path);
    const loader = routeLoaders[loaderKey];
    if (loader && !warmedChunks.has(loaderKey)) {
        warmedChunks.add(loaderKey);
        loader().catch(() => warmedChunks.delete(loaderKey));
    }

    const slug = cmsSlugForPath(path);
    if (slug) {
        preloadPage(slug);
        if (slug === 'resources') {
            preloadResources();
        }
        if (slug === 'asset-map') {
            preloadGlobeData();
        }
        if (clusterSlugs.has(slug)) {
            getCmsConstants().catch(() => undefined);
        }
    }
}

export function preloadLikelyRoutes(): void {
    ['/', '/our-approach', '/resources', '/asset-map'].forEach(preloadRoute);
}
